import React, { useState } from 'react';

import { Icon } from 'design-system-web';
import cs from 'classnames';
import { useTranslation } from 'react-i18next';

import { VisitorType } from 'lane-shared/domains/visitorManagement/types/VisitorType';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { formatIncompletePhoneNumber } from 'lane-shared/helpers/phoneNumbers';
import { VisitorManagementFeatureProperties } from 'lane-shared/types/features/VisitorManagementFeatureProperties';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';

import { Button, Thumbnail } from 'components/general';
import { H5, S } from 'components/typography';

import { isVisitorEmailConflict } from 'lane-shared/domains/visitorManagement/helpers/isVisitorEmailConflict';

import styles from './VisitorListItem.scss';

type VisitorListItemProps = {
  visitor: VisitorType;
  properties: VisitorManagementFeatureProperties;
  allowEdit?: boolean;
  onEditSave?: (visitor: VisitorType) => void;
  onDelete?: (visitor: VisitorType) => void;
  receipt?: boolean;
  submitterEmail?: string;
  hostEmail?: string;
};

const formatPhone = (phoneNumber: string): string => {
  return formatIncompletePhoneNumber(phoneNumber);
};

const generateAdditionalVisitorInfo = (visitor: VisitorType): string => {
  const visitorInfoList: string[] = [];
  const joinPattern = ' | ';

  if (visitor.email) visitorInfoList.push(visitor.email);

  if (visitor.phone) visitorInfoList.push(formatPhone(visitor.phone));

  if (visitor.company) visitorInfoList.push(visitor.company);

  return visitorInfoList.length ? visitorInfoList.join(joinPattern) : '';
};

const getWatchlistStatusMessage = (
  visitor: VisitorType,
  watchlistApprovalMessageFlag: boolean | undefined,
  t: (key: string) => string
): string => {
  if (watchlistApprovalMessageFlag) {
    return t('web.content.feature.visitorManagement.form.requiresAdminApproval');
  }

  if (visitor.approvalRequired) {
    return t('web.content.feature.visitorManagement.form.needsVerification');
  }

  return t('web.content.feature.visitorManagement.form.watchlistMatch');
};

const getEmailConflictMessageKey = (
  emailMatchesHost: boolean,
  emailMatchesSubmitter: boolean
): string | null => {
  if (emailMatchesHost) {
    return 'web.content.feature.visitorManagement.visitorInvite.form.emailMatchesHost';
  }

  if (emailMatchesSubmitter) {
    return 'web.content.feature.visitorManagement.visitorInvite.form.emailMatchesSubmitter';
  }

  return null;
};

export function VisitorListItem({
  visitor,
  allowEdit = false,
  onDelete,
  receipt = false,
  submitterEmail,
  hostEmail,
}: VisitorListItemProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useTranslation();
  const watchlistApprovalMessageFlag = useFlag(
    FeatureFlag.VisitorManagementWatchlistApprovalMessage,
    false
  );

  const visitorName = [visitor?.firstName, visitor?.lastName]
    .filter(value => Boolean(value))
    .join(' ');
  const visitorInfo = generateAdditionalVisitorInfo(visitor);
  const isWatchlisted = Boolean(
    visitor.watchlistIds?.length && visitor.watchlistIds.length > 0
  );
  const requiresApproval = visitor.approvalRequired === true;
  const hasWatchlistWarning = watchlistApprovalMessageFlag
    ? requiresApproval
    : isWatchlisted;
  const watchlistStatusMessage = getWatchlistStatusMessage(
    visitor,
    watchlistApprovalMessageFlag,
    t
  );
  const emailMatchesSubmitter = isVisitorEmailConflict(
    visitor.email,
    submitterEmail
  );
  const emailMatchesHost = isVisitorEmailConflict(visitor.email, hostEmail);
  const emailConflictMessageKey = getEmailConflictMessageKey(
    emailMatchesHost,
    emailMatchesSubmitter
  );
  const hasWarning = hasWatchlistWarning || Boolean(emailConflictMessageKey);

  const handleOnDelete = () => {
    if (onDelete) {
      onDelete(visitor);
    }
  };

  const renderDeleteButton = () => (
    <div>
      <Button
        testId="deleteButtonTrigger"
        className={styles.button}
        size="small"
        onClick={() => setIsDeleting(true)}
        startIcon={
          <Icon
            name="trash-alt"
            testId="deleteIcon"
            set={ICON_SET_FONTAWESOME}
          />
        }
      >
        {t('Remove')}
      </Button>
    </div>
  );

  const renderDeleteActionButtons = () => (
    <>
      <div>
        <Button
          testId="deleteButton"
          className={styles.button}
          size="small"
          variant="contained"
          onClick={handleOnDelete}
        >
          {t('Remove')}
        </Button>
      </div>
      <div>
        <Button
          testId="cancelDeleteButton"
          className={styles.button}
          size="small"
          onClick={() => setIsDeleting(false)}
        >
          {t('web.admin.hardware.management.hardware.button.cancel')}
        </Button>
      </div>
    </>
  );

  return (
    <div
      className={cs(styles.visitorListItem, {
        [styles.receipt]: receipt,
        [styles.warningItem]: hasWarning,
      })}
    >
      <div className={styles.user}>
        <Thumbnail className={styles.thumbnail} name={visitorName} />
        <div className={styles.textWrapper}>
          <H5 data-test="visitorName">{visitorName}</H5>
          {visitorInfo && <S data-test="visitorInfo">{visitorInfo}</S>}
          {emailConflictMessageKey ? (
            <S className={styles.warningMessage} data-test="emailConflictStatus">
              {t(emailConflictMessageKey)}
            </S>
          ) : (
            hasWatchlistWarning && (
              <S className={styles.warningMessage} data-test="watchlistStatus">
                {watchlistStatusMessage}
              </S>
            )
          )}
        </div>
      </div>
      {hasWarning && (
        <div className={styles.warningIcon}>
          <Icon
            name="exclamation-circle"
            testId="exclamation-circle"
            set={ICON_SET_FONTAWESOME}
          />
        </div>
      )}
      {allowEdit && (
        <>
          {!isDeleting && renderDeleteButton()}
          {isDeleting && renderDeleteActionButtons()}
        </>
      )}
    </div>
  );
}
